import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminCustomerList, AdminStats, api, formatDate, formatPrice } from '../../api';
import Footer from '../../Components/Footer';
import { Alert, Loading, useAction, useLoad } from '../../Components/ui';

function Stats() {
    const { data: stats } = useLoad(() => api.get<{ stats: AdminStats }>('/admin/stats').then(r => r.data.stats), []);
    const tiles = stats ? [
        { label: 'Customers', value: String(stats.customers) },
        { label: 'Active services', value: String(stats.activeServices) },
        { label: 'Setting up or failed', value: String(stats.attentionServices), warn: stats.attentionServices > 0 },
        { label: 'Paid this month', value: formatPrice(stats.revenueThisMonthOre).replace('Free', '0 kr') },
    ] : [];
    return (
        <div className="card-grid cols-4" style={{ marginBottom: '2rem' }}>
            {tiles.map(t => (
                <div key={t.label} className="card-item stat-tile">
                    <div className="kv-label">{t.label}</div>
                    <div className={`stat-value${t.warn ? ' warn' : ''}`}>{t.value}</div>
                </div>
            ))}
        </div>
    );
}

function InviteCustomer() {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState<string | null>(null);
    const { busy, error, run } = useAction();
    const submit = (e: FormEvent) => {
        e.preventDefault();
        setSent(null);
        void run(async () => {
            await api.post('/auth/admin/invite', { email });
            setSent(email);
            setEmail('');
        });
    };
    return (
        <div className="form-card">
            <h3>Invite a customer</h3>
            {error && <Alert type="error">{error}</Alert>}
            {sent && <Alert type="success">Invitation sent to {sent}.</Alert>}
            <form className="fi-form" onSubmit={submit}>
                <div className="inline-fields">
                    <label style={{ gridColumn: 'span 2' }}>
                        Email
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="customer@example.com" />
                    </label>
                    <button className="btn-submit inline" disabled={busy}>{busy ? 'Sending…' : 'Send invitation'}</button>
                </div>
            </form>
        </div>
    );
}

/** /admin — all customers with their service counts. */
export default function AdminCustomers() {
    const [params, setParams] = useSearchParams();
    const q = params.get('q') ?? '';
    const page = Math.max(1, Number(params.get('page') ?? 1));
    const [search, setSearch] = useState(q);
    useEffect(() => setSearch(q), [q]);

    const { data, error, loading } = useLoad(
        () => api.get<AdminCustomerList>('/admin/customers', { params: { q, page } }).then(r => r.data), [q, page]);
    const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

    const go = (next: { q?: string; page?: number }) => {
        const p = new URLSearchParams();
        const nq = next.q ?? q;
        if (nq) p.set('q', nq);
        if ((next.page ?? 1) > 1) p.set('page', String(next.page));
        setParams(p);
    };

    return (
        <>
            <section className="section page-section">
                <span className="section-label">Admin</span>
                <h1 className="page-title">Customers</h1>
                <p className="section-desc">Browse every customer's services, orders and payments, and open any control panel.</p>

                <Stats />

                <div className="panel-card" style={{ marginBottom: '1.5rem' }}>
                    <div className="panel-card-header">
                        <h3>{data ? `${data.total} account${data.total === 1 ? '' : 's'}` : 'Accounts'}</h3>
                        <form className="fi-form admin-search" onSubmit={e => { e.preventDefault(); go({ q: search.trim(), page: 1 }); }}>
                            <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email" />
                        </form>
                    </div>
                    {error && <Alert type="error">{error}</Alert>}
                    {loading && !data ? <Loading /> : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead><tr><th>Name</th><th>Email</th><th>Services</th><th>Needs attention</th><th>Orders</th><th>Last login</th><th>Since</th></tr></thead>
                                <tbody>
                                    {data?.customers.map(c => (
                                        <tr key={c.id}>
                                            <td>
                                                <Link className="text-link" to={`/admin/customers/${c.id}`}>{c.name}</Link>
                                                {c.role === 'admin' && <span className="tag tag-category" style={{ marginLeft: '0.5rem' }}>admin</span>}
                                            </td>
                                            <td className="mono">{c.email}</td>
                                            <td>{c.activeServices} / {c.services} active</td>
                                            <td>{c.attentionServices > 0 ? <span className="tag tag-progress">{c.attentionServices}</span> : '—'}</td>
                                            <td>{c.orders}</td>
                                            <td>{formatDate(c.lastLoginAt)}</td>
                                            <td>{formatDate(c.createdAt)}</td>
                                        </tr>
                                    ))}
                                    {data?.customers.length === 0 && <tr><td colSpan={7} className="empty-state">No customers match "{q}".</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {pages > 1 && (
                        <div className="btn-row" style={{ marginTop: '1rem', justifyContent: 'flex-end' }}>
                            <button className="btn-outline small" disabled={page <= 1} onClick={() => go({ page: page - 1 })}>← Previous</button>
                            <span className="card-text">Page {page} of {pages}</span>
                            <button className="btn-outline small" disabled={page >= pages} onClick={() => go({ page: page + 1 })}>Next →</button>
                        </div>
                    )}
                </div>

                <InviteCustomer />
            </section>
            <Footer />
        </>
    );
}
